import React, { useEffect } from 'react';
import * as d3 from 'd3';
import { rounder } from '@lib/helpers';

const HomeDonutChart = ({ chartId, data, scoreEsg, innerRadius = 75, outerRadius = 100 }) => {
  // Prepara plotData senza mutare l’oggetto `data` originale
  let plotData = [];
  let computedData = data;

  if (Object.values(data).every((num) => num === 0)) {
    plotData = [1];
    computedData = { empty: 1 };
  } else {
    plotData = Object.keys(data).map((key) => data[key]);
  }

  useEffect(() => {
    const colors = computedData.empty ? ['#e5e5e5'] : ['#689689', '#D4BE31', '#C94723'];

    const sizes = { innerRadius, outerRadius };
    const durations = { entryAnimation: 2000 };

    // Pulisci SVG
    const svgSel = d3.select(`#${chartId}`);
    svgSel.html('');

    // Generatori
    const generator = d3.pie().padAngle(0.04).sort(null);
    const chart = generator(plotData);

    // Gruppo principale
    const gRoot = svgSel.append('g').attr('transform', 'translate(100, 100)');

    // Archi
    const arcs = gRoot
      .selectAll('path')
      .data(chart)
      .enter()
      .append('path')
      .style('fill', (d, i) => colors[i])
      .attr('data-value', (d) => d.data)
      .attr('data-label', (d) => {
        const label = Object.keys(computedData).find((key) => computedData[key] === d.data);
        return label;
      });

    // Testo al centro
    gRoot
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('font-size', '2.1rem')
      .attr('y', 12)
      .attr('font-weight', 'bold')
      .text(scoreEsg);

    // Animazione
    const angleInterpolation = d3.interpolate(generator.startAngle()(), generator.endAngle()());
    const innerRadiusInterpolation = d3.interpolate(0, sizes.innerRadius);
    const outerRadiusInterpolation = d3.interpolate(0, sizes.outerRadius);

    const arc = d3.arc();

    arcs
      .transition()
      .duration(durations.entryAnimation)
      .attrTween('d', (d) => {
        const originalEnd = d.endAngle;
        return (t) => {
          const currentAngle = angleInterpolation(t);
          if (currentAngle < d.startAngle) return '';
          d.endAngle = Math.min(currentAngle, originalEnd);
          return arc(d);
        };
      });

    svgSel
      .transition()
      .duration(durations.entryAnimation)
      .tween('arcRadii', () => {
        return (t) =>
          arc.innerRadius(innerRadiusInterpolation(t)).outerRadius(outerRadiusInterpolation(t));
      });

    // --- TOOLTIP FIX ---
    // Tooltip "unico" per questo chart (evita duplicati):
    const tooltipId = `svg-tooltip-${chartId}`;

    // se esiste già lo riuso, se no lo creo
    let tooltip = d3.select(`#${tooltipId}`);
    if (tooltip.empty()) {
      tooltip = d3
        .select('body')
        .append('div')
        .attr('id', tooltipId)
        .attr('class', 'svg-tooltip')
        .style('position', 'absolute')
        .style('visibility', 'hidden')
        .style('pointer-events', 'none') // evita flickering sul mouse
        .style('z-index', 9999);
    }

    const mouseEnterCallback = (event, d) => {
      d3.select(event.currentTarget)
        .attr('stroke-width', '2')
        .attr('stroke', event.currentTarget.style.fill);

      tooltip
        .style('visibility', 'visible')
        .text(computedData.empty ? 'non applicabile' : `${rounder(d.data)}%`);
    };

    const mouseMoveCallback = (event) => {
      tooltip.style('top', `${event.pageY}px`).style('left', `${event.pageX + 10}px`);
    };

    const mouseLeaveCallback = (event) => {
      d3.select(event.currentTarget).attr('stroke-width', '0');
      tooltip.style('visibility', 'hidden');
    };

    // Nota: in d3 v6+ la firma è (event, datum)
    arcs
      .on('mouseenter', mouseEnterCallback)
      .on('mousemove', mouseMoveCallback)
      .on('mouseleave', mouseLeaveCallback);

    // Cleanup: rimuovo listeners + tooltip (o lo lascio? qui lo rimuovo per essere puliti)
    return () => {
      arcs.on('mouseenter', null).on('mousemove', null).on('mouseleave', null);
      tooltip.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <svg
        id={chartId}
        preserveAspectRatio='xMidYMid meet'
        viewBox='0 0 200 200'
        style={{
          width: '11rem',
          height: '11rem',
          display: 'block',
          flexShrink: 0,
        }}
      />
    </div>
  );
};

export default HomeDonutChart;
